/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: This file was automatically migrated to TypeScript by `dev typescript migrate`.
//  The eslint disable comment above should be removed and the resulting errors fixed.
import {DocumentNode, gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import * as OrganizationActions from 'actions/OrganizationActions';
import {TeamSettingsTeam} from 'orgs/__generated/TeamSettingsTeam';
import Avatar, {hasDefaultAvatar} from 'ui-library/avatar/Avatar';
import {AvatarShape, AvatarSize} from 'ui-library/avatar/AvatarConstants';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import TextArea from 'ui-library/bs/TextArea';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import * as nyc from 'ui-library/NYC';
import runModal from 'ui-library/runModal';

import DeleteTeamModal from './DeleteTeamModal';
import * as OrganizationUtil from './OrganizationUtil';

export const TEAM_SETTINGS_TEAM_FRAGMENT: DocumentNode = gql`
  fragment TeamSettingsTeam on TeamNode {
    id
    avatarUrl
    description
    handle
    isSchemaAdmin
    name
    permissions {
      admin
    }
  }
`;

interface Props {
  isLoading: boolean;
  team: TeamSettingsTeam;
}

type State = any;

class TeamSettingsTab extends React.Component<Props, State> {
  override state: State = {
    name: this.props.team.name,
    description: this.props.team.description,
    isSchemaAdmin: this.props.team.isSchemaAdmin,
  };
  hasAdminPermissions = this.props.team.permissions?.admin ?? false;
  disableInputs = this.props.isLoading || !this.hasAdminPermissions;

  _handleSaveTeamChanges = (e: any): void => {
    e.preventDefault();
    if (!this.disableInputs) {
      OrganizationActions.updateTeam(this.props.team.id, this.state);
    }
  };

  _updateCurrentTeam = (teamAttribute: any): void => {
    this.setState(teamAttribute);
  };

  _handleAvatarChange = (e: any): void => {
    if (e.target.files.length === 0) {
      return;
    }
    OrganizationActions.updateTeamAvatar(this.props.team.id, e.target.files[0]);
  };

  _handleRemoveAvatar = (e: any): void => {
    OrganizationActions.removeTeamAvatar(this.props.team.id);
  };

  _handleDelete = async (): Promise<void> => {
    const confirmed = await runModal(DeleteTeamModal, {team: this.props.team});
    if (confirmed) {
      OrganizationActions.deleteTeam(this.props.team.id);
    }
  };

  override render(): JSX.Element {
    return (
      <nyc.Container size="md">
        {!this.hasAdminPermissions && <p>Please contact a team admin to update the settings.</p>}
        <nyc.Row>
          <nyc.Col md={7}>
            <form onSubmit={this._handleSaveTeamChanges}>
              <FormGroup>
                <Label>Team Name</Label>
                <input
                  className="form-control"
                  value={this.state.name}
                  onChange={(e) => {
                    this._updateCurrentTeam({name: e.target.value});
                  }}
                  disabled={this.disableInputs}
                />
              </FormGroup>
              <FormGroup>
                <Label>Team Description</Label>
                <TextArea
                  rows={3}
                  value={this.state.description}
                  onChange={(e: any) => {
                    this._updateCurrentTeam({description: e.target.value});
                  }}
                  disabled={this.disableInputs}
                />
              </FormGroup>
              <FormGroup>
                <Label>Schema Permissions</Label>
                <CheckboxInput
                  className={classNames('u-flex-display', css(styles.checkbox))}
                  id="is-schema-admin"
                  checked={this.state.isSchemaAdmin}
                  onChange={(e) => {
                    this._updateCurrentTeam({isSchemaAdmin: e.target.checked});
                  }}
                  label={OrganizationUtil.getTeamSchemaAdminSettingsCopy()}
                  disabled={this.disableInputs}
                />
              </FormGroup>
              <Button
                buttonStyle={ButtonStyle.PRIMARY}
                disabled={this.disableInputs}
                size={ButtonSize.MEDIUM}
                text={this.props.isLoading ? 'Loading...' : 'Save Changes'}
                type="submit"
              />
            </form>
          </nyc.Col>
          <nyc.Col md={4}>
            <div>
              <h5 className="u-no-spacing-top">Team Avatar</h5>
              <Avatar
                className="u-small-spacing-bottom"
                size={AvatarSize.XLARGE}
                shape={AvatarShape.SQUARE}
                entity={this.props.team}
              />
              <input
                className="u-small-spacing-bottom"
                type="file"
                onChange={this._handleAvatarChange}
                disabled={this.disableInputs}
              />
              {!hasDefaultAvatar(this.props.team) && this.hasAdminPermissions && (
                <a onClick={this._handleRemoveAvatar}>Remove avatar</a>
              )}
            </div>
            <hr />
            {this.hasAdminPermissions && (
              <div>
                <a className="organizationSettings-delete" onClick={this._handleDelete}>
                  Delete Team
                </a>
              </div>
            )}
          </nyc.Col>
        </nyc.Row>
      </nyc.Container>
    );
  }
}

const styles = StyleSheet.create({
  checkbox: {
    whiteSpace: 'pre-wrap',
  },
});

export default TeamSettingsTab;
